import _ from 'underscore';
import { useContext, useMemo } from 'react';
import { ServerContext } from '@bingads-webui-campaign-react/server-context';
import {
  useCustomerLevelVariables,
  usePilotsValue,
} from '@bingads-webui-campaign-react/basic-context-utils';
import { useCurrencies } from '@bingads-webui/overreact-cc';
import { useCurrencyCountryMapping } from '@bingads-webui-campaign-react/use-currency-country-mapping';
import { useCreateHookResultPromise } from '@bingads-webui-campaign-react/common-hooks';

import { usePreference, preferenceNames } from './use-preference';

/**
 * Hook that returns a promise-based loader for country-filtered currencies.
 * Mirrors the currency logic in useBudgetRepository (pmax-budget) without the full
 * campaign/asset-group/location loading overhead.
 *
 * Returns `loadCurrencies()` — a function that returns a Promise resolving with
 * `{ currencies, currency, defaultCurrency }` once async data is ready.
 * This allows wizard repository `load()` callbacks to `await` the result.
 *
 * Filtering chain (same as useBudgetRepository):
 *   1. getCurrenciesForCountry(selectedCountry) → allowed currency codes for the country
 *   2. Apply Google/Facebook auth restrictions → effectiveAllowedCodes
 *   3. useCurrencies() → all currency objects { value: { Code, DisplayName } }
 *   4. Filter full list by effectiveAllowedCodes
 *   5. Resolve active currency from effectiveAllowedCodes (not the raw country set)
 * @returns {Function} loadCurrencies — call to get a Promise resolving with { currencies, currency, defaultCurrency }
 */
export const useCurrenciesByCountry = () => {
  const serverContext = useContext(ServerContext);
  const customerLevelVariables = useCustomerLevelVariables();
  const isGoogleAuthIntlNewSignInPageEnabled = usePilotsValue('GoogleAuthIntlNewSignInPage');

  const [{ value: countryPreference }] = usePreference(preferenceNames.SelectedCountry);
  const [{ value: currencyPreference }] = usePreference(preferenceNames.SelectedCurrency);

  const {
    getCurrenciesForCountry,
    getDefaultCurrencyForCountry,
    isUseCurrencyCountryMappingReady,
  } = useCurrencyCountryMapping({ customerLevelVariables });

  const [{ data: allCurrencies }] = useCurrencies(customerLevelVariables);

  // Build the resolved result reactively — once both data sources are ready,
  // this becomes a truthy object that useCreateHookResultPromise will resolve.
  const resolvedResult = useMemo(() => {
    if (!allCurrencies || !isUseCurrencyCountryMappingReady) return null;

    const country = countryPreference || 'US';
    let allowedCodes = getCurrenciesForCountry(country) || [];

    // Apply Google/Facebook auth restrictions (same as useBudgetRepository)
    if (['Google', 'Facebook'].includes((serverContext as any).AuthType)) {
      if (isGoogleAuthIntlNewSignInPageEnabled) {
        allowedCodes = _.filter(allowedCodes, code => !['CNY', 'INR'].includes(code));
      } else {
        allowedCodes = ['USD'];
      }
    }

    // Filter and sort currency objects by allowed codes (same as useBudgetRepository)
    const currencies = _.sortBy(
      _.filter(allCurrencies, cur => allowedCodes.includes(cur.value.Code)),
      cur => cur.value.DisplayName
    );

    // Resolve the active currency — mirrors useBudgetRepository priority:
    //   1. user's saved preference (if allowed)
    //   2. country default currency (if allowed)
    //   3. first in the allowed list
    const defaultCurrency = getDefaultCurrencyForCountry(country) || 'USD';
    let currency = _.contains(allowedCodes, currencyPreference)
      ? currencyPreference
      : defaultCurrency;
    if (!_.contains(allowedCodes, currency)) {
      currency = allowedCodes[0] || 'USD';
    }

    return { currencies, currency, defaultCurrency };
  }, [
    allCurrencies, isUseCurrencyCountryMappingReady, countryPreference,
    getCurrenciesForCountry, getDefaultCurrencyForCountry, currencyPreference,
    (serverContext as any).AuthType,
    isGoogleAuthIntlNewSignInPageEnabled,
  ]);

  // Returns a function: `loadCurrencies()` → Promise<{ currencies, currency, defaultCurrency }>
  // The promise resolves once both useCurrencies and useCurrencyCountryMapping are ready.
  return useCreateHookResultPromise({ result: resolvedResult });
};
